import React, { useState } from 'react';
import { Upload, FileText, BarChart3, Download, RefreshCw } from 'lucide-react';
import FileUpload from '../components/FileUpload';
import PolicyTable from '../components/PolicyTable';
import AggregateDataTable from '../components/AggregateDataTable';
import Footer from '../components/Footer';
import { usePolicies } from '../context/PoliciesContext';

const DashboardPage = () => {
  const [activeTab, setActiveTab] = useState('policies');
  const { policies, isProcessing, handleFileUpload } = usePolicies();

  const tabs = [
    { id: 'policies', label: 'Policy Table', icon: FileText },
    { id: 'aggregate', label: 'Aggregate Data', icon: BarChart3 }
  ];

  return (
    <div className="h-screen flex flex-col w-full">
      <div className="flex-1 space-y-6 px-4 sm:px-6 lg:px-8 pb-8 bg-white">
        <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-quickbooks-gray-dark">Upload documents and analyze revenue streams</p>
          </div>
          <div className="flex items-center space-x-3">
            <button className="text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2" style={{backgroundColor: '#ff5252'}} onMouseEnter={(e) => e.target.style.backgroundColor = '#e53e3e'} onMouseLeave={(e) => e.target.style.backgroundColor = '#ff5252'}>
              <RefreshCw className="h-4 w-4" />
              <span>Refresh</span>
            </button>
            <button className="btn-primary flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* File Upload Section */}
        <div className="card">
          <div className="flex items-center space-x-3 mb-4">
            <Upload className="h-6 w-6 text-enpact-red" />
            <h2 className="text-xl font-semibold">Document Upload</h2>
          </div>
          <p className="text-quickbooks-gray-dark mb-4">
            Upload PDF, PNG, or Word documents for NLP processing. The system will extract policy information and calculate revenue streams.
          </p>
          <FileUpload onUpload={handleFileUpload} isProcessing={isProcessing} />
        </div>

        {/* Processing Status */}
        {isProcessing && (
          <div className="card bg-blue-50 border-blue-200">
            <div className="flex items-center space-x-3">
              <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
              <div>
                <h3 className="font-semibold text-blue-900">Processing Documents</h3>
                <p className="text-blue-700">Extracting policy information using DeepSeek NLP...</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Section */}
        {policies.length > 0 && (
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold">Analysis Results</h2>
              <div className="text-sm text-quickbooks-gray-dark">
                {policies.length} policies processed
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-quickbooks-border mb-6">
              <nav className="flex space-x-8">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === tab.id
                          ? 'border-enpact-red text-enpact-red'
                          : 'border-transparent text-quickbooks-gray-dark hover:text-quickbooks-blue hover:border-quickbooks-blue'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]">
              {activeTab === 'policies' && (
                <PolicyTable policies={policies} />
              )}
              {activeTab === 'aggregate' && (
                <AggregateDataTable policies={policies} />
              )}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!isProcessing && policies.length === 0 && (
          <div className="card text-center py-12">
            <Upload className="h-16 w-16 text-quickbooks-gray-dark mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Data Available</h3>
            <p className="text-quickbooks-gray-dark mb-4">
              Upload documents to start analyzing revenue streams
            </p>
          </div>
        )}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto">
        <Footer />
      </div>
    </div>
  );
};

export default DashboardPage;
